import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { settings } from "@krea/customer-features/settings";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import CustomerServiceContactResponsiveDialog from "@krea/customer-features/shared-components/modals/CustomerServiceContact";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useModalStore } from "@krea/customer-features/store/modal/hooks";
import {
  LoanApplicationStates,
  LoanApplicationVerificationFailureReasons,
} from "@krea/customer-features/types/loanApplication";
import { APPLICATION_NAME } from "@krea/customer-features/utils";

import InfoBox from "../info-box";
import { SubmitApplicationAndDeleteDuplicatesModal } from "../modals";
import TextActionLink from "../text/TextActionLink";
import { TOAST_TYPE } from "../toast";
import { Toast } from "../toast/Toast";
import { ToastAdditionalContent } from "../toast/ToastAdditionalContent";
import { ToastContactAdditionalOptionsBelow } from "../toast/ToastContactAdditionalOptionsBelow";
import { ToastWithContactPerson } from "../toast/ToastWithContactPerson";

interface ILoanApplicationMessagesProps {
  hasBids?: boolean;
  isSubmitApplicationModal?: boolean;
  hasUserDismissedImprovements?: boolean;
}

const LoanApplicationMessages = ({
  hasBids,
  isSubmitApplicationModal,

  hasUserDismissedImprovements = false,
}: ILoanApplicationMessagesProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [openCustomerServiceContactModal, setOpenCustomerServiceContactModal] =
    useState(false);

  const [toastProps, setToastProps] = useState<any>(null);
  const [toastType, setToastType] = useState(TOAST_TYPE.NORMAL_TOAST);

  const { data: currentLoanApplication } = useGetCachedCurrentLoanApplication();
  const openModal = useModalStore((store) => store.openModal);

  const isCustomerWeb = settings.appName === APPLICATION_NAME.CUSTOMER_WEB;
  const {
    state,
    biddingEndsAt,
    verificationFailureReasons,
    latestDuplicatedApplicationInfo,
  } = currentLoanApplication ?? {};

  const biddingClosed = biddingEndsAt
    ? dayjs(biddingEndsAt).isBefore(dayjs())
    : false;

  const contactCustomerService = (
    <Trans i18nKey="dash.bidsRejected.paragraphWithLinktoSupport">
      Placeholder for first text
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
      <a
        href="#"
        className="tw-font-bold"
        onClick={() => {
          setOpenCustomerServiceContactModal(true);
        }}
      >
        Placeholder for second text
      </a>
    </Trans>
  );

  useEffect(() => {
    switch (state) {
      case LoanApplicationStates.CREATED:
        // Application not submitted automatically
        const toastPropsToSet: any = {
          isWarning: true,
          innerText: {
            title: t("dash.messages.applicationNotSubmitted.title"),
            subtitle: t("dash.messages.applicationNotSubmitted.subtitle"),
          },
        };

        // Not submitted due to a duplicate application
        if (
          verificationFailureReasons?.includes(
            LoanApplicationVerificationFailureReasons.DUPLICATE_EXISTS,
          )
        ) {
          toastPropsToSet.innerText = {
            title: t("dash.messages.applicationNotSubmitted.duplicate.title"),
            subtitle: isSubmitApplicationModal
              ? ""
              : t("dash.messages.applicationNotSubmitted.duplicate.subtitle"),
          };
          toastPropsToSet.additionalOptions = [];

          if (!isSubmitApplicationModal)
            toastPropsToSet.additionalOptions = [
              {
                text: t(
                  "dash.messages.applicationNotSubmitted.duplicate.continueWithApplication.text",
                  {
                    creationDate:
                      latestDuplicatedApplicationInfo?.submittedAt &&
                      new Date(
                        latestDuplicatedApplicationInfo.submittedAt,
                      ).toLocaleDateString(),
                    noActiveOffers: latestDuplicatedApplicationInfo?.numBids,
                  },
                ),
                action: (
                  <TextActionLink
                    value={t(
                      "dash.messages.applicationNotSubmitted.duplicate.continueWithApplication.actionText",
                    )}
                    onClick={() =>
                      isCustomerWeb
                        ? navigate(
                            `/current-application/${latestDuplicatedApplicationInfo?.id}`,
                          )
                        : navigate(
                            `/loan-applications/${latestDuplicatedApplicationInfo?.id}`,
                          )
                    }
                  />
                ),
              },
              {
                text: t(
                  "dash.messages.applicationNotSubmitted.duplicate.submitNewApplication.text",
                ),
                action: (
                  <TextActionLink
                    value={t(
                      "dash.messages.applicationNotSubmitted.duplicate.submitNewApplication.actionText",
                    )}
                    onClick={() =>
                      openModal({
                        modal: <SubmitApplicationAndDeleteDuplicatesModal />,
                        closeOnBackgroundClick: false,
                      })
                    }
                  />
                ),
              },
            ];
          setToastType(TOAST_TYPE.TOAST_CONTACT_ADDITIONAL_BELOW);
        }

        // No duplicate application and not a submit application modal
        else if (!isSubmitApplicationModal) {
          setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON);
        }

        setToastProps(toastPropsToSet);
        break;

      case LoanApplicationStates.OPEN_FOR_BIDS:
        if (!hasBids && biddingClosed) {
          setToastProps({
            isWarning: true,
            innerText: {
              title: t("dash.noBidsMessage"),
              subtitle: t("dash.noBidsImproveApplicationRecommend"),
            },
          });
        } else {
          setToastProps(null);
        }

        break;

      case LoanApplicationStates.REJECTED:
        setToastProps({
          isWarning: true,
          innerText: {
            title: `${t("dash.rejectedState.heading")}.`,
            subtitle: t("dash.rejectedMessage"),
          },
        });

        if (!isSubmitApplicationModal) {
          setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON);
        }
        break;

      case LoanApplicationStates.BIDS_REJECTED_BY_CUSTOMER:
        setToastProps({
          innerText: {
            title: t("dash.bidsRejected.heading"),
            subtitle: contactCustomerService,
          },
        });
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, hasUserDismissedImprovements]);

  if (state === LoanApplicationStates.NO_BIDS_RECEIVED) {
    return (
      <LayoutOneColumn wide>
        <InfoBox
          title={t("dash.noBidsMessage")}
          subtitle={t("dash.noBidsImproveApplicationRecommend")}
          buttonProps={[
            {
              onClick: () => navigate(isCustomerWeb ? "/application" : "/new"),
              text: t("bidDetails.paidOutStartNew"),
            },
          ]}
        />
      </LayoutOneColumn>
    );
  }

  if (!toastProps) {
    return null;
  }

  return (
    <>
      {toastProps &&
        {
          [TOAST_TYPE.NORMAL_TOAST]: (
            <>
              <Toast {...toastProps} />
              {state === LoanApplicationStates.BIDS_REJECTED_BY_CUSTOMER && (
                <CustomerServiceContactResponsiveDialog
                  open={openCustomerServiceContactModal}
                  setOpen={setOpenCustomerServiceContactModal}
                />
              )}
            </>
          ),
          [TOAST_TYPE.TOAST_WITH_CONTACT_PERSON]: (
            <ToastWithContactPerson {...toastProps} />
          ),
          [TOAST_TYPE.TOAST_CONTACT_ADDITIONAL_BELOW]: (
            <ToastContactAdditionalOptionsBelow {...toastProps} />
          ),
          [TOAST_TYPE.TOAST_ADDITIONAL_CONTENT]: (
            <ToastAdditionalContent {...toastProps} />
          ),
        }[toastType]}
    </>
  );
};

export default LoanApplicationMessages;
